import { isValidElement, useState, useRef, useMemo, useEffect, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { FILTER_SELECTION_TYPES, FILTER_DISPLAY } from "app/features/product/productFilters";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Radio } from "app/components/form/Radio";
import { useFormikContext } from "formik";
import classNames from "classnames";
import "./FacetFilter.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import Searchbar from "app/pages/HotelOnly/components/Searchbar/Searchbar";
import { useIntersectionObserver, useDebouncedFuzzySearch } from "app/hooks";
import { useIntl, FormattedMessage } from "react-intl";
import { Slider } from "app/components/ui/Slider/Slider";
import { FormikChoiceButton } from "app/components/form/FormikChoiceButton";
import { Field, FieldLabel } from "app/components/form/Field/Field";
import { CheckboxField } from "app/components/form/CheckboxField/CheckboxField";

const isJSX = value => isValidElement(value);

const OPTIONS_BATCH_SIZE = 30;

export const FacetFilter = ({ filter }) => {
	const {
		field,
		isSearchable,
		options,
		range,
		search,
		selectionMode,
		title,
		formatRangeValue,
		rangeThumbCount,
		display,
	} = filter;

	const { track } = useAnalytics();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useState("");
	const hasStartedTypingRef = useRef(false);
	const [visibleCount, setVisibleCount] = useState(OPTIONS_BATCH_SIZE);

	const handleQueryChange = e => {
		const newValue = e.target.value;

		if (!hasStartedTypingRef.current && newValue?.length > 0) {
			hasStartedTypingRef.current = true;
			track("user_search_filter_with_searchbar", {
				filterId: field,
			});
		}

		setQuery(newValue);
	};

	const fuzzyOptions = useDebouncedFuzzySearch(options, query, {
		keys: ["label"],
	});

	const filteredOptions = useMemo(() => {
		if (!isSearchable) {
			return options;
		}

		const trimmedQuery = query.trim().toLowerCase();

		if (!trimmedQuery) {
			return options;
		}

		return fuzzyOptions.map(option => option.item);
	}, [query, options, isSearchable, fuzzyOptions]);

	const paginatedOptions = useMemo(
		() => filteredOptions.slice(0, visibleCount),
		[filteredOptions, visibleCount]
	);

	const canLoadMore = visibleCount < filteredOptions.length;

	const scrollContainerRef = useRef(null);
	const [scrollRootEl, setScrollRootEl] = useState(null);

	useLayoutEffect(() => {
		// Ce useLayoutEffect permet de déterminer le root de l'observer : <ul> si il scroll (desktop popover), sinon viewport (mobile drawer scroll).
		const el = scrollContainerRef.current;
		if (selectionMode === FILTER_SELECTION_TYPES.RANGE || !el) {
			return;
		}

		const isScrollable = el.scrollHeight > el.clientHeight;

		if (isScrollable) {
			setScrollRootEl(el);
		}
	}, [selectionMode, visibleCount, canLoadMore]);

	const [sentinelRef, inView] = useIntersectionObserver({
		root: scrollRootEl,
		rootMargin: "400px 0px",
		threshold: 0,
		disabled: !canLoadMore,
	});

	useEffect(() => {
		setVisibleCount(OPTIONS_BATCH_SIZE);
	}, [query, filteredOptions.length]);

	useEffect(() => {
		if (!canLoadMore || !inView) {
			return;
		}

		setVisibleCount(prev => Math.min(prev + OPTIONS_BATCH_SIZE, filteredOptions.length));
	}, [inView, canLoadMore, filteredOptions.length]);

	return (
		<>
			{isSearchable && (
				<header className="facet-filter__search">
					<Searchbar
						className="facet-filter__searchbar"
						value={query}
						onChange={handleQueryChange}
						placeholder={formatMessage({
							id: search?.placeholderKey || "sdp.search.cta.label",
						})}
					/>
				</header>
			)}

			<fieldset>
				<legend className="sr-only">{title}</legend>

				<Content
					display={display}
					title={title}
					selectionMode={selectionMode}
					options={paginatedOptions}
					range={range}
					filterId={field}
					formatRangeValue={formatRangeValue}
					rangeThumbCount={rangeThumbCount}
					scrollContainerRef={scrollContainerRef}
					sentinelRef={canLoadMore ? sentinelRef : undefined}
				/>
			</fieldset>
		</>
	);
};

FacetFilter.propTypes = {
	filter: PropTypes.object.isRequired,
};

const parseRangeValue = (selectedRangeValue, range, rangeThumbCount = 2) => {
	const domainMin = range?.min ?? 0;
	const domainMax = range?.max ?? 0;

	const fallback = rangeThumbCount === 1 ? [domainMax] : [domainMin, domainMax];

	if (typeof selectedRangeValue !== "string") {
		return fallback;
	}

	const match = selectedRangeValue.match(/^between_(\d+)_([\d]+)$/);

	if (!match) {
		return fallback;
	}

	const [, minStr, maxStr] = match;

	const min = Number(minStr);
	const max = Number(maxStr);

	if (!Number.isFinite(min) || !Number.isFinite(max)) {
		return fallback;
	}

	if (rangeThumbCount === 2) {
		return [min, max];
	}

	return [max];
};

const Content = ({
	title,
	selectionMode,
	options,
	range,
	filterId,
	formatRangeValue,
	rangeThumbCount,
	display,
	scrollContainerRef,
	sentinelRef,
}) => {
	const { setFieldValue, values } = useFormikContext();

	const rangeValue = parseRangeValue(values?.[filterId]?.[0], range, rangeThumbCount);

	const hasOptions = Array.isArray(options) && options.length > 0;

	if (selectionMode !== FILTER_SELECTION_TYPES.RANGE && !hasOptions) {
		return (
			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				component="p"
				className="facet-filter__options facet-filter__no-results"
			>
				<FormattedMessage id="faq.search.no.result" />
			</Typography>
		);
	}

	switch (selectionMode) {
		case FILTER_SELECTION_TYPES.MULTIPLE:
			return (
				<ul
					ref={scrollContainerRef}
					className={classNames("facet-filter__options", {
						"facet-filter__options--buttons": display === FILTER_DISPLAY.BUTTONS,
					})}
				>
					{options.map(option => {
						const { value, label, labelPrefix, count, showCount } = option;

						return (
							<li
								key={`${filterId}--${String(value)}`}
								className={classNames("facet-filter__option", {
									"facet-filter__option--disabled": count === 0,
								})}
							>
								{display === FILTER_DISPLAY.DEFAULT && (
									<Field className="facet-filter__checkbox">
										<CheckboxField
											id={`${filterId}--${String(value)}`}
											name={filterId}
											value={String(value)}
											disabled={count === 0}
										/>

										<FieldLabel
											htmlFor={`${filterId}--${String(value)}`}
											className="facet-filter__checkbox-label"
										>
											{isJSX(labelPrefix) && labelPrefix}

											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												component="span"
											>
												{label}
											</Typography>

											{Number.isFinite(count) && showCount && (
												<Typography
													variant={TYPOGRAPHY_VARIANTS.REGULAR}
													component="span"
													className="facet-filter__count-text"
												>
													{count}
												</Typography>
											)}
										</FieldLabel>
									</Field>
								)}

								{display === FILTER_DISPLAY.BUTTONS && (
									<FormikChoiceButton
										type="checkbox"
										name={filterId}
										value={String(value)}
										label={label}
										disabled={count === 0}
									/>
								)}
							</li>
						);
					})}

					{sentinelRef && (
						<li className="facet-filter__sentinel" aria-hidden="true">
							<div ref={sentinelRef} />
						</li>
					)}
				</ul>
			);
		case FILTER_SELECTION_TYPES.SINGLE:
			return (
				<ul ref={scrollContainerRef} className="facet-filter__options">
					{options.map(option => {
						const { value, label, labelPrefix, count, showCount } = option;

						return (
							<li
								key={`${filterId}--${String(value)}`}
								className={classNames("facet-filter__option", {
									"facet-filter__option--disabled": count === 0,
								})}
							>
								{display === FILTER_DISPLAY.DEFAULT && (
									<Radio
										name={filterId}
										value={String(value)}
										className="facet-filter__radio"
										disabled={count === 0}
									>
										<div className="facet-filter__radio-content">
											{isJSX(labelPrefix) && labelPrefix}

											<Typography
												variant={TYPOGRAPHY_VARIANTS.REGULAR}
												component="span"
											>
												{label}
											</Typography>

											{Number.isFinite(count) && showCount && (
												<Typography
													variant={TYPOGRAPHY_VARIANTS.REGULAR}
													component="span"
													className="facet-filter__count-text"
												>
													{count}
												</Typography>
											)}
										</div>
									</Radio>
								)}

								{display === FILTER_DISPLAY.BUTTONS && (
									<FormikChoiceButton
										type="radio"
										name={filterId}
										value={String(value)}
										label={label}
										disabled={count === 0}
									/>
								)}
							</li>
						);
					})}

					{sentinelRef && (
						<li className="facet-filter__sentinel" aria-hidden="true">
							<div ref={sentinelRef} />
						</li>
					)}
				</ul>
			);
		case FILTER_SELECTION_TYPES.RANGE:
			if (!Number.isFinite(range?.min) || !Number.isFinite(range?.max)) {
				return null;
			}

			return (
				<>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						component="p"
						className="facet-filter__range-title"
					>
						{title}
					</Typography>

					<Slider
						value={rangeValue}
						min={range.min}
						max={range.max}
						formatValue={formatRangeValue}
						onValueChange={newValue => {
							const domainMin = range.min;
							const domainMax = range.max;

							let newMin = domainMin;
							let newMax = domainMax;

							if (rangeThumbCount === 2) {
								const a = newValue?.[0];
								const b = newValue?.[1];

								if (!Number.isFinite(a) || !Number.isFinite(b)) {
									setFieldValue(filterId, []);
									return;
								}

								newMin = Math.min(a, b);
								newMax = Math.max(a, b);
							} else {
								const x = newValue?.[0];
								if (!Number.isFinite(x)) {
									setFieldValue(filterId, []);
									return;
								}

								newMin = domainMin;
								newMax = x;
							}

							const isBackToDefaultRange =
								newMin === domainMin && newMax === domainMax;
							if (isBackToDefaultRange) {
								setFieldValue(filterId, []);
								return;
							}

							setFieldValue(filterId, [`between_${newMin}_${newMax}`]);
						}}
					/>
				</>
			);
		default:
			return null;
	}
};

Content.propTypes = {
	filterId: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	selectionMode: PropTypes.string.isRequired,
	options: PropTypes.array,
	range: PropTypes.object,
	formatRangeValue: PropTypes.func,
	rangeThumbCount: PropTypes.number,
	display: PropTypes.string,
	scrollContainerRef: PropTypes.shape({ current: PropTypes.object }),
	sentinelRef: PropTypes.func,
};
